import { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useCallStore } from '@/store/callStore';
import { useAuthStore } from '@/store/authStore';
import Avatar from '@/components/ui/Avatar';
import type { CallPeer } from '@/api/calls';

/**
 * Full-screen call overlay. Rendered unconditionally at the App root and
 * self-hides when `phase === 'idle'`. Covers every phase of a 1:1 call:
 *
 *   - incoming:   ring screen with accept / reject,
 *   - outgoing:   ring screen with cancel,
 *   - connecting: "Соединение…" banner,
 *   - active:     remote video (for VIDEO mode) + local preview + controls,
 *   - ended:      brief "Разговор завершён" splash before auto-reset.
 */
export default function CallOverlay() {
  const {
    call,
    phase,
    mode,
    error,
    localStream,
    remoteStream,
    micEnabled,
    cameraEnabled,
    pendingPeerName,
    acceptIncoming,
    rejectIncoming,
    cancelOutgoing,
    hangup,
    toggleMic,
    toggleCamera,
  } = useCallStore();
  const me = useAuthStore((s) => s.user);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Re-run when phase changes because video/audio elements mount/unmount
  // based on showVideo which depends on phase.
  useEffect(() => {
    const el = localVideoRef.current;
    if (el && localStream) {
      el.srcObject = localStream;
      el.play().catch(() => {});
    }
  }, [localStream, phase]);

  useEffect(() => {
    const vid = remoteVideoRef.current;
    if (vid && remoteStream) {
      vid.srcObject = remoteStream;
      vid.play().catch(() => {});
    }
    const aud = remoteAudioRef.current;
    if (aud && remoteStream) {
      aud.srcObject = remoteStream;
      aud.play().catch(() => {});
    }
  }, [remoteStream, phase]);

  if (phase === 'idle') return null;

  const me_id = me?.id ?? '';
  const peer: CallPeer | null = call
    ? call.callerId === me_id
      ? call.callee
      : call.caller
    : null;
  const peerName = peer?.displayName ?? peer?.username ?? pendingPeerName ?? '…';

  const statusLabel =
    error                    ? error :
    phase === 'incoming'     ? (mode === 'VIDEO' ? 'Входящий видеозвонок' : 'Входящий звонок') :
    phase === 'outgoing'     ? (mode === 'VIDEO' ? 'Исходящий видеозвонок' : 'Исходящий звонок') :
    phase === 'connecting'   ? 'Соединение…' :
    phase === 'active'       ? 'В разговоре' :
    phase === 'ended'        ? 'Разговор завершён' :
    '';

  const showVideo = mode === 'VIDEO' && (phase === 'active' || phase === 'connecting');

  return (
    <div className="fixed inset-0 z-[100] bg-surface-950/95 backdrop-blur-sm flex flex-col">
      {/* ── Remote media ─────────────────────────────────────────────────── */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {showVideo ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover bg-black"
          />
        ) : (
          // Audio-only or pre-connect: show a big avatar centered.
          <div className="flex flex-col items-center gap-6">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-surface-800 shadow-2xl">
              <Avatar src={peer?.avatarUrl ?? null} username={peer?.username ?? '?'} size="lg" />
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-surface-50">{peerName}</h2>
              {peer && (
                <p className="text-surface-400 text-sm font-mono mt-1">@{peer.username}</p>
              )}
            </div>
          </div>
        )}

        {/* Remote audio — always mounted, styled invisible (not display:none
            which can block playback in some browsers). */}
        <audio ref={remoteAudioRef} autoPlay playsInline className="absolute w-0 h-0 opacity-0" />

        {/* Top status bar */}
        <div className="absolute top-6 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none">
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-900/80 backdrop-blur">
            {phase === 'incoming' && <PhoneIncoming size={14} className="text-brand-400" />}
            {phase === 'outgoing' && <PhoneOutgoing size={14} className="text-brand-400" />}
            {phase === 'active' && <Phone size={14} className="text-emerald-400" />}
            <span className="text-sm text-surface-100">{statusLabel}</span>
          </div>
          {phase === 'active' && <ActiveTimer />}
        </div>

        {/* Local preview (only meaningful for video) */}
        {showVideo && (
          <div className="absolute bottom-6 right-6 w-32 h-44 md:w-44 md:h-56 rounded-lg overflow-hidden border-2 border-surface-800 shadow-xl bg-black">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover scale-x-[-1]"
            />
          </div>
        )}
      </div>

      {/* ── Control bar ──────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-surface-800 bg-surface-900/90 backdrop-blur p-6">
        <div className="flex items-center justify-center gap-6">
          {/* Accept (only during incoming) */}
          {phase === 'incoming' && (
            <button
              onClick={acceptIncoming}
              className="w-16 h-16 rounded-full bg-emerald-600 hover:bg-emerald-500 flex items-center justify-center text-white shadow-lg transition-colors"
              title={mode === 'VIDEO' ? 'Принять видеозвонок' : 'Принять звонок'}
            >
              {mode === 'VIDEO' ? <Video size={24} /> : <Phone size={24} />}
            </button>
          )}

          {/* Mic toggle (only during active) */}
          {(phase === 'connecting' || phase === 'active') && (
            <button
              onClick={toggleMic}
              className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${
                micEnabled ? 'bg-surface-700 hover:bg-surface-600' : 'bg-red-600 hover:bg-red-500'
              }`}
              title={micEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
            >
              {micEnabled ? <Mic size={22} /> : <MicOff size={22} />}
            </button>
          )}

          {/* Hangup / Reject / Cancel */}
          <button
            onClick={
              phase === 'incoming' ? rejectIncoming :
              phase === 'outgoing' ? cancelOutgoing :
              hangup
            }
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white shadow-lg transition-colors"
            title={
              phase === 'incoming' ? 'Отклонить' :
              phase === 'outgoing' ? 'Отменить' :
              'Завершить'
            }
          >
            <PhoneOff size={24} />
          </button>

          {/* Camera toggle (video calls during active only) */}
          {mode === 'VIDEO' && (phase === 'connecting' || phase === 'active') && (
            <button
              onClick={toggleCamera}
              className={`w-14 h-14 rounded-full flex items-center justify-center text-white transition-colors ${
                cameraEnabled ? 'bg-surface-700 hover:bg-surface-600' : 'bg-red-600 hover:bg-red-500'
              }`}
              title={cameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
            >
              {cameraEnabled ? <Video size={22} /> : <VideoOff size={22} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ActiveTimer() {
  const answeredAt = useCallStore((s) => s.call?.answeredAt);
  const since = answeredAt ? new Date(answeredAt).getTime() : Date.now();
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const elapsed = Math.max(0, Math.floor((Date.now() - since) / 1000));
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  const label = `${m}:${s.toString().padStart(2, '0')}`;
  return (
    <div className="px-3 py-1 rounded-full bg-surface-900/70 text-surface-300 text-xs tabular-nums">
      {label}
    </div>
  );
}
